import React, { useEffect, useMemo, useState } from "react";
import styles from "./Inventory.module.scss";
import { ProductsTable } from "./components/ProductsTable/ProductsTable";
import { Header } from "./components/Header/Header";
import { FilterPanel } from "./components/FilterPanel/FilterPanel";
import { useGetInventory } from "../../utils/react-query/queryInventory/queries";
import { useFilterStore } from "../../supplierEcommerce/store";
import type { InventoryProduct } from "./components/ProductsTable/types";

const uniqueValues = (values: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(values.filter((value): value is string => Boolean(value)))
  );

const toFilterOptions = (values: string[]): FilterOption[] =>
  values.map((value) => ({ value, label: value }));

export const deriveInventoryFilterOptions = (
  products: InventoryProduct[] = []
): FilterOptions[] => [
  {
    id: "category",
    name: "Category",
    options: toFilterOptions(
      uniqueValues(products.flatMap((product) => product.categories ?? []))
    ),
  },
  {
    id: "line",
    name: "Line",
    options: toFilterOptions(
      uniqueValues(products.map((product) => product.line))
    ),
  },
  {
    id: "brand",
    name: "Brand",
    options: toFilterOptions(
      uniqueValues(products.map((product) => product.brand))
    ),
  },
];

const Inventory: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const clearFilters = useFilterStore((state) => state.clearFilters);
  const { data: inventoryData } = useGetInventory({
    search: searchQuery || undefined,
  });

  // Reset filters on mount so stale ecommerce filters don't bleed into inventory
  useEffect(() => {
    clearFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filterOptions = useMemo(
    () => deriveInventoryFilterOptions(inventoryData?.products),
    [inventoryData?.products]
  );

  return (
    <div className={styles.container}>
      <Header
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
        isFilterOpen={isFilterOpen}
      />

      <div className={styles.tableContainer}>
        <ProductsTable searchQuery={searchQuery} />
      </div>

      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        options={filterOptions}
      />
    </div>
  );
};

export default Inventory;
